import { useCallback, useEffect, useRef, useState } from 'react';

import { getSignupAbandonOnLOHPTimestamp } from 'bundles/authentication-common/utils/coachStorageUtils';
import { isUserLoggedOut } from 'bundles/front-page/utils/coachStorageUtils';

export type LOHPCoachSignupAbandonTriggerResult = {
  shouldShowCoach: boolean;
  isFirstTimeTrigger: boolean;
};

/**
 * Hook to determine if Coach prompt pills should be shown based on Coach Signup Abandonment experiment logic:
 * - A logged-out learner returns to LOHP after abandoning the signup process on LOHP
 *
 * @returns {shouldShowCoach, isFirstTimeTrigger} indicating whether to show Coach and whether this is the first trigger
 */
export const useLOHPCoachSignupAbandonTrigger = (): LOHPCoachSignupAbandonTriggerResult => {
  const [shouldShowCoach, setShouldShowCoach] = useState(false);
  const [isFirstTimeTrigger, setIsFirstTimeTrigger] = useState(false);
  const previousSignupAbandonOnLOHPTimestamp = useRef<number | null>(null);

  const signupAbandonOnLOHPTimestamp = getSignupAbandonOnLOHPTimestamp();

  const performSignalChecks = useCallback<() => void>(() => {
    if (!isUserLoggedOut()) {
      setShouldShowCoach(false);
      setIsFirstTimeTrigger(false);
      return;
    }

    if (!getSignupAbandonOnLOHPTimestamp()) {
      setShouldShowCoach(false);
      setIsFirstTimeTrigger(false);
      return;
    }

    setShouldShowCoach(true);

    // Only set isFirstTimeTrigger if this is the first time condition is met
    // (the previous signup abandon on LOHP timestamp is null)
    setIsFirstTimeTrigger(previousSignupAbandonOnLOHPTimestamp.current === null);
  }, []);

  // Perform signal check on page load and when signup abandon on LOHP timestamp changes from null to a value or vice versa
  useEffect(() => {
    if (previousSignupAbandonOnLOHPTimestamp.current !== signupAbandonOnLOHPTimestamp) {
      if (previousSignupAbandonOnLOHPTimestamp.current === null || signupAbandonOnLOHPTimestamp === null) {
        performSignalChecks();
      }
      previousSignupAbandonOnLOHPTimestamp.current = signupAbandonOnLOHPTimestamp;
    }
  }, [performSignalChecks, signupAbandonOnLOHPTimestamp]);

  return { shouldShowCoach, isFirstTimeTrigger };
};
